import { useEffect, useMemo, useRef, useState } from "react";
import Timeline from "./components/Timeline";
import TrackPlayPoint from "./components/TrackPlayPoint";
import trackStateStore from "@/stores/clip/trackState";
import playerStore from "@/stores/clip/player";
import { getGridPixel } from "@/utils/canvasUtil";
import "./index.less";
import { parseUrl } from "@/utils/file";
import { VideoGroup } from "@/stores/clip/class/VideoTrack";
import ZoomControl from "./components/zoomControl";
import { observer } from "mobx-react";
import TrackLine from "./components/TrackLine";
import { debounce } from "@/utils";
import CanvasPlayer from "./components/Player/CanvasPlayer";
import useDragListener from "./hooks/useDrag";
import { v4 as uuid } from "uuid";
import { MaterialTypeEnum } from "@/stores/clip/data/constant";
import { StickerGroup } from "@/stores/clip/class/StickerTrack";
import { EffectGroup } from "@/stores/clip/class/EffectTrack";
import { BackgroundGroup } from "@/stores/clip/class/BackgroundTrack";
import { useSearchParams } from "react-router-dom";
import useDragTrack from "./hooks/useDragTrack";
import { TextGroup } from "@/stores/clip/class/TextTrack";
import { PicInPicGroup } from "@/stores/clip/class/PicInPicTrack";
import Menu from "./components/Menu";
import { AudioGroup } from "@/stores/clip/class/AudioTrack";
import { ThumbGroup } from "@/stores/clip/class/ThumbTrack";

export interface ClipProps {
  clip?: boolean;
  data?: any;
}

const Clip = function (props: ClipProps) {
  const { clip: isClip = true, data = {} } = props;
  const [searchParams] = useSearchParams();
  const { onMouseDown, onMouseMove, onMouseUp } = useDragTrack();
  const [lastScrollTop, setLastScrollTop] = useState(0);

  useDragListener({
    onDragEnd: async (data) => {
      if (!data) return;
      console.log("onDragEnd data", data);

      if (
        data.sourceData.type === MaterialTypeEnum.video ||
        data.sourceData.type === MaterialTypeEnum.image
      ) {
        const videoMaterial = {
          type:
            data.sourceData.type === MaterialTypeEnum.video ? "video" : "image",
          id: uuid(),
          url:
            data.sourceData.play_info.play_url ||
            data.sourceData.play_info.cover_url,
          name: data.sourceData.name,
          fps: +(data.sourceData.fps || 0),
          format: data.sourceData.format_name,
          width: data.sourceData.width,
          height: data.sourceData.height,
          duration: data.sourceData.duration || 5000,
        };
        if (data.line != null && data.index != null) {
          trackStateStore.addMaterial(data.line, data.index, videoMaterial);
        } else {
          const start = trackStateStore.getFrameCount();
          trackStateStore.addGroup(new VideoGroup([videoMaterial], { start }));
        }

        trackStateStore.adjustVideoTrack();
      } else if (data.sourceData.type === MaterialTypeEnum.sticker) {
        const stickerGroup = {
          id: uuid(),
          url: data.sourceData.cover_url,
          name: data.sourceData.name,
          format: data.sourceData.format_name || "png",
          width: data.sourceData.width,
          height: data.sourceData.height,
        };
        const stickerTrack = new StickerGroup([stickerGroup], {
          start: playerStore.playStartFrame,
        });
        if (data.index == null) {
          trackStateStore.addGroup(stickerTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, stickerGroup);
        }
      } else if (data.sourceData.type === MaterialTypeEnum.effect) {
        const effectGroup = {
          id: uuid(),
          name: data.sourceData.name,
        };
        const effectTrack = new EffectGroup([effectGroup], {
          start: playerStore.playStartFrame,
        });
        if (data.index == null) {
          trackStateStore.addGroup(effectTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, effectTrack);
        }
      } else if (data.sourceData.type === MaterialTypeEnum.background) {
        const backgroundGroup = {
          id: uuid(),
        };
        const backgroundTrack = new BackgroundGroup([backgroundGroup], {
          start: playerStore.playStartFrame,
        });
        if (data.index == null) {
          trackStateStore.addGroup(backgroundTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, backgroundTrack);
        }
      } else if (data.sourceData.type === MaterialTypeEnum.text) {
        const textGroup = {
          id: uuid(),
        };
        const backgroundTrack = new TextGroup([textGroup], {
          start: playerStore.playStartFrame,
        });
        if (data.index == null) {
          trackStateStore.addGroup(backgroundTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, backgroundTrack);
        }
      } else if (data.sourceData.type === MaterialTypeEnum.picInPic) {
        const picInPicGroup = {
          id: uuid(),
        };
        const picInPicTrack = new PicInPicGroup([picInPicGroup], {
          start: playerStore.playStartFrame,
        });
        if (data.index == null) {
          trackStateStore.addGroup(picInPicTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, picInPicTrack);
        }
      } else if (data.sourceData.type === MaterialTypeEnum.audio) {
        const audioGroup = {
          id: uuid(),
        };
        const audioTrack = new AudioGroup([audioGroup], {
          start: playerStore.playStartFrame,
        });
        if (data.index == null) {
          trackStateStore.addGroup(audioTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, audioTrack);
        }
      } else if (data.sourceData.type === MaterialTypeEnum.thumb) {
        const thumbGroup = {
          id: uuid(),
          url: data.sourceData.url,
        };
        const thumbTrack = new ThumbGroup([thumbGroup], {
          start: 0,
        });
        if (data.index == null) {
          trackStateStore.addGroup(thumbTrack, data.line, data.position);
        } else {
          trackStateStore.addMaterial(data.line, data.index, thumbTrack);
        }
      }
    },
  });

  const trackListContainer = useRef(null);
  const trackList = useRef(null);
  const [dropLineIndex, setDropLineIndex] = useState(-1);
  const [insertBefore, setInsertBefore] = useState(true);
  const offsetLine = {
    left: 10, // 容器 margin, 为了显示拖拽手柄
    right: 200,
  };
  const [startX, setStartX] = useState(0 - offsetLine.left);

  const trackStyle = useMemo(() => {
    return {
      flexShrink: 0,
      width:
        getGridPixel(
          trackStateStore.trackScale,
          trackStateStore.getFrameCount()
        ) + offsetLine.right,
    };
  }, [trackStateStore.trackScale, trackStateStore.getFrameCount()]);

  async function onUpload() {
    // 需要将获取图片文件转换为ImageTractItem
    // 必须：图片的format、height、width、sourceFrame
    // 非必须：cover信息（如果是gif图片）
    // const files = await selectFile({ accept: ".mp4", multiple: false });
    // const file = files[0];

    // const file = await getFileByUrl(url, "test.mp4");
    // const start = performance.now();
    // // TODO：生成md5时间，已经大于解析视频时间，需要优化
    // const id = await getMD5(await file.arrayBuffer());
    // console.log("生成md5耗时", performance.now() - start, "ms");
    // console.log("url-------", searchParams.get("url"));

    // const clip = await videoDecoder.decode({
    //   id,
    //   url,
    //   // stream: file.stream(),
    //   // type: file.type,
    // });
    // console.log("解析视频耗时", performance.now() - start, "ms", clip);
    // if (!clip) {
    //   // 提示解析视频失败
    //   alert("解析视频失败");
    //   return;
    // }

    // const {
    //   id: id2,
    //   file: file2,
    //   clip: clip2,
    // } = await parseUrl(
    //   "https://zhaixing-dev-00.oss-cn-shanghai.aliyuncs.com/test/media/18d6fc78-18cb-4a38-a382-04f52fafa825.mp4"
    // );

    if (isClip) {
      if (!data) return;
      console.log("url=========", data?.play_info?.play_url);

      const { id, file, clip } = await parseUrl(data?.play_info?.play_url);
      const videoTrack = new VideoGroup(
        [
          {
            id,
            url: URL.createObjectURL(file),
            name: data.name,
            format: file.type,
            width: clip.meta.width,
            height: clip.meta.height,
            duration: Math.round(clip.meta.duration / 1000),
          },
          // {
          //   id: id2,
          //   url: URL.createObjectURL(file2),
          //   name: file2.name,
          //   format: file2.type,
          //   width: clip2.meta.width,
          //   height: clip2.meta.height,
          //   duration: Math.round(clip2.meta.duration / 1e6),
          // },
        ],
        { start: playerStore.playStartFrame, name: data.name }
      );
      videoTrack.resize({
        width: playerStore.playerConfig.playerWidth,
        height: playerStore.playerConfig.playerHeight,
      });
      trackStateStore.addGroup(videoTrack);
    } else {
      for (let i = 0; i < 3; i++) {
        const start = trackStateStore.getFrameCount();
        const videoTrack = new VideoGroup([], { start });
        trackStateStore.addGroup(videoTrack);
      }
    }
  }

  let maxDelta = 0;

  const setScale = debounce(() => {
    trackStateStore.trackScale -= maxDelta > 0 ? 10 : -10;
    maxDelta = 0;
  }, 100);

  const handleWheel = (event: any) => {
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (maxDelta === 0) {
        maxDelta = event.deltaY;
      }
      setScale();
    }
  };
  function handleScroll() {
    // document.getElementById("track-menu").style.overflowY = "auto";
    const {
      scrollLeft,
      scrollTop,
      clientWidth,
      clientHeight,
      scrollWidth,
      scrollHeight,
    } = trackList.current;
    console.log("scrollWidth", scrollTop, scrollTop > lastScrollTop);

    setStartX(scrollLeft - offsetLine.left);

    if (scrollTop > lastScrollTop) {
      trackStateStore.setScrollData({
        direction: "down",
        scrollTop,
        scrollX: scrollWidth > clientWidth, // 是否出现横向滚动条
        scrollY: scrollHeight > clientHeight, // 是否出现纵向滚动条
        style: {
          position: "absolute",
          top: scrollTop + 30,
        },
      });
    } else {
      trackStateStore.setScrollData({
        direction: "up",
        scrollTop,
        scrollX: scrollWidth > clientWidth,
        scrollY: scrollHeight > clientHeight, // 是否出现纵向滚动条
        style: {
          position: "absolute",
          bottom: scrollHeight - (scrollTop + clientHeight),
        },
      });
    }

    setLastScrollTop(scrollTop);
    // setTimeout(() => {
    //   document.getElementById("track-menu").style.overflowY = "hidden";
    // }, 100);
  }

  function setSelectTract(event: any, line: number, index: number) {
    event.preventDefault();
    event.stopPropagation();
    // trackStateStore.selectGroupItem.line = line;
    // trackStateStore.selectGroupItem.index = index;
    trackStateStore.setSelectTrackItem({
      line,
      index,
    });
  }

  function handleSelectFrame(frame: number) {
    const playFrame = frame - 1;
    const startFrane =
      playFrame < 0
        ? 0
        : playFrame > trackStateStore.getFrameCount()
          ? trackStateStore.getFrameCount()
          : playFrame;
    playerStore.isPause = true;
    playerStore.playStartFrame = startFrane;
  }

  useEffect(() => {
    trackStateStore.setIsClip(isClip);
    // 模拟加载视频素材
    onUpload();
  }, [isClip, data?.play_info?.play_url]);

  return (
    // <div
    //   style={{
    //     display: "flex",
    //     height: "100vh",
    //     flexDirection: "column",
    //     background: "rgb(32 32 35)",
    //     padding: 20,
    //     color: "#fff",
    //     boxSizing: "border-box",
    //   }}
    // >
    //   <div style={{ flex: 1 }}>

    //     <Button
    //       style={{ position: "absolute", right: 40, top: 60 }}
    //       onClick={onUpload}
    //     >
    //       添加视频
    //     </Button>

    //     <Button
    //       style={{ position: "absolute", right: 40, top: 60 }}
    //       onClick={() => {
    //         console.log(trackStateStore.trackList);
    //       }}
    //     >
    //       保存
    //     </Button>
    //   </div>

    <div
      style={{
        display: "flex",
        height: "100%",
        flexDirection: "column",
        background: "rgb(32 32 35)",
        color: "#fff",
        boxSizing: "border-box",
      }}
    >
      {isClip && <CanvasPlayer />}
      <ZoomControl
        value={trackStateStore.trackScale}
        onChange={(v) => {
          trackStateStore.setTrackScale(v);
        }}
      />
      <div
        style={{
          display: "flex",
          // flex: !isClip ? 1 : "none",
          overflow: "auto",
          flex: 1,
        }}
      >
        <Menu />
        <div
          ref={trackList}
          className="track-list"
          style={{
            position: "relative",
            width: "100%",
            overflowX: "auto",
            overflowY: "auto",
            // flex: isClip ? "none" : 1,
            // height: isClip ? "300px" : "inherit",
          }}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onClick={(e) => setSelectTract(e, -1, -1)}
        >
          <Timeline
            start={startX}
            scale={trackStateStore.trackScale}
            style={{ height: "30px" }}
            onSelectFrame={handleSelectFrame}
          />

          <div
            style={{
              display: "flex",
              padding: "30px 0 0",
              position: "absolute",
              minWidth: "100%",
              minHeight: "calc(100% - 30px)",
            }}
            ref={trackListContainer}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            <div id="track-container" style={trackStyle}>
              {trackStateStore.showTrackList.map((lineData, lineIndex) => (
                <TrackLine
                  key={lineData.groups.reduce((r, item) => r + item.id, "line")}
                  style={{
                    marginLeft: `${offsetLine.left}px`,
                  }}
                  className={`${dropLineIndex === lineIndex ? (insertBefore ? "showLine-t" : "showLine-b") : ""}`}
                  lineType={lineData.type}
                  lineIndex={lineIndex}
                  isMain={lineData.main}
                  lineData={lineData.groups}
                />
              ))}
            </div>
            <TrackPlayPoint />

            {/* 辅助线 */}
            {!!trackStateStore.showTrackList.length &&
              trackStateStore.dragData.fixLines
                .reduce((r, item) => r.concat(item), [])
                .map((line, i) => (
                  <div
                    key={i}
                    className="absolute bottom-0 z-30 w-px bg-yellow-300 pointer-events-none -top-5 dark:bg-yellow-300"
                    style={{
                      position: "absolute",
                      width: "1px",
                      top: "-4px",
                      bottom: 0,
                      zIndex: 30,
                      pointerEvents: "none",
                      left: `${line.position + 10}px`,
                      background: "yellow",
                    }}
                  ></div>
                ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(Clip);
